import { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '~/lib/api';
import { formatDate } from '~/lib/date';
import { messages, useT } from '~/i18n';
import { EmptyState, PageHeader, Tone, WaitingInline } from '~/components/ui';
import type { ToneName } from '~/components/ui';

interface Application {
  _id: string;
  company: string;
  title: string;
  status: string;
  /**
   * Cuándo SALIÓ. Existe sólo si de verdad salió: una fila en cola o en borrador
   * no la tiene, y esta lista está llena de esas. Era la causa del "Invalid Date"
   * en todas las filas — se pintaba `new Date(undefined)`.
   */
  sent_at: string | null;
  /** Cuándo la preparamos. El modelo la estampa siempre; es la que pinta el Panel. */
  created_at: string | null;
}

interface HistoryResponse {
  applications: Application[];
  total: number;
  page: number;
  totalPages: number;
}

/** Mismo semáforo que el panel: mint lo que contestó, coral lo que se cayó. */
const STATUS_TONES: Record<string, ToneName> = {
  SENT: 'info',
  REPLIED: 'good',
  INTERVIEW: 'good',
  REJECTED: 'bad',
  QUEUED: 'muted',
  NO_RESPONSE: 'warn',
};

/** Los `value` que se envían al API; el texto vive en `workspace.history.filter`. */
const FILTER_OPTIONS = ['', 'SENT', 'REPLIED', 'INTERVIEW', 'REJECTED', 'QUEUED', 'NO_RESPONSE'] as const;

const LIMIT = 20;

export default function JobApplyHistoryPage() {
  // `useT` se suscribe al idioma; `~/lib/date` lo lee al llamarse.
  const t = useT('workspace').history;
  const [applications, setApplications] = useState<Application[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchApplications = useCallback(async (p: number, s: string) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: String(p),
        limit: String(LIMIT),
      });
      if (s) params.set('status', s);
      const res = await apiFetch(`/job-apply/api/applications?${params}`);
      if (!res.ok) throw new Error(messages().workspace.history.httpError(res.status));
      const data: HistoryResponse = await res.json();
      setApplications(data.applications);
      setTotalPages(data.totalPages);
      setTotal(data.total);
    } catch (err: any) {
      setError(err.message || messages().workspace.history.loadError);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchApplications(page, status);
  }, [page, status, fetchApplications]);

  const onStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatus(e.target.value);
    setPage(1);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        scope="TBit Jobs"
        // "Postulaciones", no "Historial": un historial suena a archivo muerto y
        // esta lista es lo que está pasando ahora mismo — la mitad de las filas
        // esperan respuesta. El nav dice lo mismo.
        title={t.title}
        actions={
          <select
            className="select select-sm"
            aria-label={t.filterLabel}
            value={status}
            onChange={onStatusChange}
          >
            {FILTER_OPTIONS.map((value) => (
              <option key={value} value={value}>
                {value ? t.filter[value] : t.filter.all}
              </option>
            ))}
          </select>
        }
      />

      {error && <div className="alert alert-error text-sm">{error}</div>}

      <div className="card">
        <div className="card-body p-0">
          {loading ? (
            <WaitingInline />
          ) : applications.length === 0 ? (
            <EmptyState
              icon={status ? 'search' : 'send'}
              title={status ? t.emptyFilteredTitle : t.emptyTitle}
              note={status ? t.emptyFilteredNote : t.emptyNote}
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t.company}</th>
                    <th>{t.position}</th>
                    <th>{t.statusHeader}</th>
                    <th>{t.date}</th>
                  </tr>
                </thead>
                <tbody>
                  {applications.map((app) => (
                    <tr key={app._id}>
                      <td className="font-medium">{app.company}</td>
                      <td>{app.title}</td>
                      <td>
                        <Tone tone={STATUS_TONES[app.status] ?? 'muted'}>
                          {t.status[app.status] || app.status}
                        </Tone>
                      </td>
                      {/* La fecha que existe, en ese orden: cuándo salió, y si
                          todavía no sale, cuándo la preparamos. Si ninguna de
                          las dos vale, una raya — nunca "Invalid Date". */}
                      <td className="hush text-sm">{formatDate(app.sent_at ?? app.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm hush">
            {t.total(total)}
          </p>
          <div className="join">
            <button
              className="join-item btn btn-sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              {t.previous}
            </button>
            <button className="join-item btn btn-sm btn-disabled">
              {page} / {totalPages}
            </button>
            <button
              className="join-item btn btn-sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              {t.next}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
